import type { DienstType, PloegSoort, ShiftType } from '@streep/types'
import type { DayCode } from './shift-roster'
import { defaultRosterPresetForDienstType, rosterPresetsForDienstType } from './shift-roster'

/**
 * FO-367 (batch-42) — gedeelde rotatiepatroon-bouwstenen voor de
 * `RotatiePatroonEditor` (`@streep/ui`). De klant-wizard (`apps/klant`) en de
 * backoffice-werktijden-tab (`apps/backoffice`) putten hier uit dezelfde bron,
 * zodat beide dezelfde presetknoppen tonen (geen drift, zelfde geest als de
 * picker-/DatePicker-regels in CLAUDE.md).
 *
 * De presets komen sinds de werkschema-presets-fix (2026-07) 1:1 uit de
 * rooster-engine (`ROSTER_PRESET_LIST` via `rosterPresetsForDienstType`) — de
 * eerdere "enkel"/"blok"-afleiding (1-2 dagen per ploegsoort) beschreef een
 * dág-rotatie (3-ploegen: ma=V di=L wo=N) terwijl 2-/3-ploegen in
 * werkelijkheid én in de engine per WEEK rouleren (21-daagse cyclus: week 1
 * Vroeg, week 2 Laat, week 3 Nacht, weekend vrij). Eén presetbron = wizard,
 * werktijden-tab én werkbon-create-modal tonen dezelfde patronen.
 *
 * `getDefaultRotations` verhuisde hierheen uit `apps/api/src/lib/shift-defaults.ts`
 * zodat de presets uit die basisreeks afgeleid worden zonder de patronen te
 * dupliceren; de api re-exporteert de functie nog.
 */

/** Geldige DayCodes (V=Vroeg, L=Laat, N=Nacht, D=Dag, X=Vrij) als tuple. */
export const DAY_CODE_VALUES = ['V', 'L', 'N', 'D', 'X'] as const

/**
 * Rotatie-basisreeksen per diensttype: per ploegen-variant de ploegsoort-
 * volgorde waarin gerouleerd wordt (één ploegsoort per rotatieblok). Eerste
 * variant = default.
 */
export function getDefaultRotations(dienstType: DienstType): PloegSoort[][] {
  switch (dienstType) {
    case 'DAGDIENST':
      return [['DAG']]
    case 'PLOEGEN_2':
      return [
        ['VROEG', 'LAAT'],
        ['LAAT', 'VROEG'],
      ]
    case 'PLOEGEN_3':
      return [
        ['VROEG', 'LAAT', 'NACHT'],
        ['VROEG', 'NACHT', 'LAAT'],
        ['LAAT', 'VROEG', 'NACHT'],
        ['LAAT', 'NACHT', 'VROEG'],
      ]
    case 'PLOEGEN_4':
      return [
        ['DAG', 'LAAT', 'NACHT', 'DAG'],
        ['VROEG', 'LAAT', 'NACHT', 'DAG'],
      ]
    case 'PLOEGEN_5':
      return [
        ['VROEG', 'LAAT', 'NACHT', 'DAG', 'DAG'],
        ['LAAT', 'VROEG', 'NACHT', 'DAG', 'DAG'],
      ]
    // PR-4 (spec 02 §3.3): consignatie heeft geen ploegrotatie — de werkbon-
    // create-flow genereert dayEntries direct uit consignatieType.
    case 'CONSIGNATIE':
      return [['NACHT']]
  }
}

/** ShiftType (werkschema) → DienstType (rooster-engine). */
function shiftTypeNaarDienstType(shiftType: ShiftType): DienstType {
  return shiftType === 'DAG' ? 'DAGDIENST' : shiftType
}

export interface RotatiePatroonPreset {
  /** Stabiele sleutel (React-keys/tests/i18n) — de engine-preset-key. */
  code: string
  /** De cyclus als DayCode-array (het opslagformaat). */
  pattern: DayCode[]
  /** Klantvriendelijk NL-label; UI's met i18n vertalen op `code`. */
  label: string
}

/**
 * Klantvriendelijke labels per engine-preset-key. De engine-labels zelf zijn
 * planner-jargon ("nacht zo–do, zondagnacht-start"); dit is de korte vorm voor
 * presetknoppen. Ontbreekt een key (nieuwe engine-preset), dan valt
 * `getRotatiePatroonPresets` terug op het engine-label.
 */
const PRESET_LABELS: Record<string, string> = {
  PLOEGEN_2_STANDAARD: 'Vroeg → Laat (per week)',
  PLOEGEN_2_VARIANT_LV: 'Laat → Vroeg (per week)',
  PLOEGEN_3_STANDAARD: 'Vroeg → Laat → Nacht (per week)',
  PLOEGEN_3_VARIANT_VLN: 'Vroeg → Laat → Nacht (per week)',
  PLOEGEN_3_VARIANT_NLV: 'Nacht → Laat → Vroeg (per week)',
  PLOEGEN_4_STANDAARD: 'Dag → Laat → Nacht (4 dagen per dienst)',
  PLOEGEN_5_STANDAARD: 'Vroeg → Laat → Nacht (volcontinu)',
  PLOEGEN_5_VARIANT_A: 'Met dagdiensten (volcontinu)',
}

/**
 * Rotatie-presets voor een ploegensysteem — 1:1 de rooster-engine-presets
 * (`rosterPresetsForDienstType`), ontdubbeld op identiek patroon (de engine
 * houdt bv. `PLOEGEN_3_VARIANT_VLN` aan als alias van `PLOEGEN_3_STANDAARD`;
 * twee identieke knoppen tonen heeft geen zin). DAGDIENST heeft geen rotatie
 * → lege lijst.
 */
export function getRotatiePatroonPresets(shiftType: ShiftType): RotatiePatroonPreset[] {
  const engine = rosterPresetsForDienstType(shiftTypeNaarDienstType(shiftType))
  const out: RotatiePatroonPreset[] = []
  const gezien = new Set<string>()
  for (const preset of engine) {
    const sleutel = preset.pattern.join('')
    if (gezien.has(sleutel)) continue
    gezien.add(sleutel)
    out.push({
      code: preset.key,
      pattern: [...preset.pattern],
      label: PRESET_LABELS[preset.key] ?? preset.label,
    })
  }
  return out
}

/**
 * Het default-rotatiepatroon (eerste engine-preset) voor een ploegensysteem,
 * of null voor DAGDIENST. Gebruikt door de klant-wizard om bij het kiezen van
 * een ploegensysteem direct het juiste patroon voor te vullen — zelfde bron
 * als de seeding op de backoffice-werktijden-tab (`defaultRosterFor`), zodat
 * beide kanten met hetzelfde patroon starten.
 */
export function getDefaultRotatiePatroon(
  shiftType: ShiftType,
): { pattern: DayCode[]; cycleDays: number } | null {
  const preset = defaultRosterPresetForDienstType(shiftTypeNaarDienstType(shiftType))
  if (!preset) return null
  return { pattern: [...preset.pattern], cycleDays: preset.cycleDays }
}

/**
 * Parse een (mogelijk oude, vrij ingevoerde) rotatiepatroon-tekstlijst naar
 * DayCode[]. Elk token wordt genormaliseerd (uppercase); tokens die volledig
 * uit geldige DayCode-letters bestaan (bv. "VLN", "V", "X") worden per letter
 * uitgevouwen. Niet-herkende tokens ("Vroeg", "2V", "8") zetten `onherkend`.
 *
 * Pre-batch-42 payloads sloegen het rotatiepatroon als vrije lettertekst op; de
 * nieuwe blokken-editor toont het geparste patroon, of een lege editor + hint
 * wanneer niets herkend werd (FO-367 §7.1).
 */
export function parseDayCodePatroon(tokens: readonly unknown[]): {
  pattern: DayCode[]
  onherkend: boolean
} {
  const geldig = new Set<string>(DAY_CODE_VALUES)
  const pattern: DayCode[] = []
  let onherkend = false
  for (const token of tokens) {
    // Defensief: een oude JSON-payload kan niet-strings bevatten (getal/null).
    if (typeof token !== 'string') {
      onherkend = true
      continue
    }
    const t = token.trim().toUpperCase()
    if (t === '') continue
    if ([...t].every((ch) => geldig.has(ch))) {
      for (const ch of t) pattern.push(ch as DayCode)
    } else {
      onherkend = true
    }
  }
  return { pattern, onherkend }
}

/** True als het patroon minstens één werkdienst bevat (niet enkel 'X'). */
export function patroonHeeftDienst(pattern: readonly DayCode[]): boolean {
  return pattern.some((c) => c !== 'X')
}
